import React from 'react';
import {BrandSafetyPrompt, EskimiAdvertiser} from '../../types';
import CountryMultiSelect from '../CountryMultiSelect';
import CategoryExclusionSelect from '../CategoryExclusionSelect';
import {MAX_BLUEPRINT_DESCRIPTION} from '../../constants/validation';
import {Toggle} from '../ui/toggle';
import {Button} from '../ui/button';
import MatchInsightsPanel from './MatchInsightsPanel';
import {BlueprintFormData, MatchInsights} from './types';
import {useBackdropDismiss} from '../../hooks/useBackdropDismiss';

interface FormUser {
    role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
}

interface BlueprintFormModalProps {
    open: boolean;
    editingPrompt: BrandSafetyPrompt | null;
    user: FormUser | null | undefined;
    isLoadingModal: boolean;
    contentLoadError: string | null;
    // deepcontext-33p5.2: validation/save/AI errors raised while the modal is
    // open, shown above the footer instead of the page banner behind the modal.
    formError?: string | null;
    formData: BlueprintFormData;
    setFormData: (data: BlueprintFormData) => void;
    allCategoriesExcluded: boolean;
    syncToIndex: boolean;
    setSyncToIndex: (v: boolean) => void;
    syncToEquativ: boolean;
    setSyncToEquativ: (v: boolean) => void;
    syncToEquativPublic: boolean;
    setSyncToEquativPublic: (v: boolean) => void;
    syncToPubmatic: boolean;
    setSyncToPubmatic: (v: boolean) => void;
    advertiserSearchTerm: string;
    setAdvertiserSearchTerm: (v: string) => void;
    loadingAdvertisers: boolean;
    advertisers: EskimiAdvertiser[];
    isRewriting: boolean;
    isLoadingMatchInsights: boolean;
    matchInsights: MatchInsights | null;
    onGetMatchInsights: () => void;
    onRewriteWithAI: () => void;
    // Generate with AI (bd deepcontext-0445.2): Write / Generate mode switch.
    contentMode: 'write' | 'generate';
    setContentMode: (mode: 'write' | 'generate') => void;
    brandUrl: string;
    setBrandUrl: (v: string) => void;
    isGeneratingPrompt: boolean;
    onGenerateWithAI: () => void;
    onSave: () => void;
    onClose: () => void;
    onSelectAdvertiser: (advertiser: EskimiAdvertiser) => void;
    onClearAdvertiser: () => void;
}

// Locked modal chrome (page lock 7, deepcontext-pnxk): 18px-radius surface,
// pill inputs at the 42px row-control standard, uppercase micro-labels,
// gradient primary + ghost cancel.
const FIELD_LABEL = 'mb-1 block text-xs font-bold uppercase tracking-[0.5px] text-ink-muted';
const PILL_INPUT =
    'w-full h-[42px] rounded-full border border-line bg-transparent px-4 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent';

const SYNC_DESTINATIONS = (props: BlueprintFormModalProps) => [
    {label: 'Index Exchange', checked: props.syncToIndex, onChange: props.setSyncToIndex},
    {label: 'Equativ (Eskimi only)', checked: props.syncToEquativ, onChange: props.setSyncToEquativ},
    {label: 'Equativ (public)', checked: props.syncToEquativPublic, onChange: props.setSyncToEquativPublic},
    {label: 'PubMatic', checked: props.syncToPubmatic, onChange: props.setSyncToPubmatic},
];

// Create/edit Blueprint modal. Extracted from PromptManager.tsx
// (deepcontext-ityf decomposition); restyled to the locked design in
// deepcontext-pnxk — behavior unchanged.
const BlueprintFormModal: React.FC<BlueprintFormModalProps> = (props) => {
    const {
        open,
        editingPrompt,
        user,
        isLoadingModal,
        contentLoadError,
        formError,
        formData,
        setFormData,
        allCategoriesExcluded,
        advertiserSearchTerm,
        setAdvertiserSearchTerm,
        loadingAdvertisers,
        advertisers,
        isRewriting,
        isLoadingMatchInsights,
        matchInsights,
        onGetMatchInsights,
        onRewriteWithAI,
        contentMode,
        setContentMode,
        brandUrl,
        setBrandUrl,
        isGeneratingPrompt,
        onGenerateWithAI,
        onSave,
        onClose,
        onSelectAdvertiser,
        onClearAdvertiser,
    } = props;

    const backdropDismiss = useBackdropDismiss(props.onClose);

    if (!open) return null;

    const canSync = user?.role === 'admin' || user?.role === 'adops';

    return (
        // Click-away cancel: clicking the backdrop closes the modal as
        // Cancel (discard, no save) — same effect as the Cancel button. Only a
        // full click on the backdrop counts (useBackdropDismiss), so a drag
        // that ends there doesn't discard the form.
        <div
            className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50"
            {...backdropDismiss}
            data-testid="edit-modal-backdrop"
        >
            <div
                data-testid="blueprint-modal-surface"
                className="relative top-10 mx-auto max-h-[90vh] w-[800px] overflow-y-auto rounded-[18px] border border-line bg-surface p-5 shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
                {/* Loading Overlay */}
                {isLoadingModal && (
                    <div
                        className="absolute inset-0 z-10 flex items-center justify-center rounded-[18px] bg-surface bg-opacity-75">
                        <div className="flex flex-col items-center gap-3">
                            <div
                                className="h-8 w-8 animate-spin rounded-full border-b-2 border-accent"></div>
                            <span className="text-sm text-ink-muted">Loading blueprint data...</span>
                        </div>
                    </div>
                )}

                <div className="mb-4 flex items-center gap-2">
                    <h3 className="text-[15px] font-extrabold text-ink">
                        {editingPrompt ? 'Edit Brand Blueprint' : 'Create New Brand Blueprint'}
                    </h3>
                    {editingPrompt && (
                        <span className="text-xs font-semibold text-ink-muted">ID: {editingPrompt.id}</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="ml-auto text-ink-muted hover:text-ink"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSave();
                }} className="space-y-3.5">
                    {/* Basic Info Section */}
                    <div className="grid grid-cols-2 gap-2.5">
                        <div>
                            <label htmlFor="name" className={FIELD_LABEL}>
                                Name *
                            </label>
                            <input
                                type="text"
                                id="name"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="e.g., Standard Brand Safety"
                                className={PILL_INPUT}
                            />
                        </div>
                        <div>
                            <label htmlFor="description" className={FIELD_LABEL}>
                                Description
                            </label>
                            <input
                                type="text"
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Brief description"
                                maxLength={MAX_BLUEPRINT_DESCRIPTION}
                                className={PILL_INPUT}
                            />
                            <p className={`mt-1 text-right text-xs ${formData.description.length >= MAX_BLUEPRINT_DESCRIPTION ? 'text-risk' : 'text-ink-muted'}`}>
                                {formData.description.length}/{MAX_BLUEPRINT_DESCRIPTION}
                            </p>
                        </div>
                    </div>

                    {/* Targeting & Association */}
                    <div className="grid grid-cols-2 gap-2.5">
                        <div>
                            <label className={FIELD_LABEL}>
                                Countries
                            </label>
                            <CountryMultiSelect
                                value={formData.countryCodes}
                                onChange={(countryCodes) => setFormData({...formData, countryCodes})}
                                placeholder="All countries"
                            />
                        </div>
                        {canSync && (
                            <div className="relative">
                                <label htmlFor="advertiserId" className={FIELD_LABEL}>
                                    Advertiser
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="advertiserId"
                                        placeholder="Search advertiser..."
                                        value={advertiserSearchTerm}
                                        onChange={(e) => setAdvertiserSearchTerm(e.target.value)}
                                        className={PILL_INPUT}
                                    />
                                    {formData.advertiserId && (
                                        <button
                                            type="button"
                                            onClick={onClearAdvertiser}
                                            aria-label="Clear advertiser"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round"
                                                      strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {loadingAdvertisers && !formData.advertiserId && (
                                    <p className="mt-1 text-xs text-ink-muted">Searching...</p>
                                )}
                                {advertisers.length > 0 && !formData.advertiserId && (
                                    <div
                                        className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-line bg-surface shadow-lg">
                                        {advertisers.map((advertiser) => (
                                            <div
                                                key={advertiser.id}
                                                onClick={() => onSelectAdvertiser(advertiser)}
                                                className="cursor-pointer px-3.5 py-2 text-xs text-ink hover:bg-ghost-bg"
                                            >
                                                {advertiser.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sync destinations — four mini gradient toggles */}
                    {canSync && (
                        <div>
                            <span className={FIELD_LABEL}>Sync destinations</span>
                            <div className="flex flex-wrap gap-x-5 gap-y-2">
                                {SYNC_DESTINATIONS(props).map(({label, checked, onChange}) => (
                                    <label key={label} className="flex items-center gap-2 text-sm font-semibold text-ink-muted">
                                        <Toggle
                                            checked={checked}
                                            onChange={onChange}
                                            aria-label={label}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Category Exclusion */}
                    <CategoryExclusionSelect
                        value={formData.excludedCategories}
                        onChange={(excludedCategories) => setFormData({...formData, excludedCategories})}
                    />

                    {/* Blueprint Content Section */}
                    <div className="border-t border-line pt-3.5">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <label htmlFor="prompt" className={`${FIELD_LABEL} mb-0`}>
                                Blueprint Content *
                            </label>
                            <div className="flex items-center gap-2">
                                {contentMode === 'write' && (
                                    <button
                                        type="button"
                                        onClick={onRewriteWithAI}
                                        disabled={isRewriting || !formData.prompt?.trim()}
                                        className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-accent-active bg-transparent px-3.5 py-1.5 text-xs font-bold text-accent-active hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isRewriting ? (
                                            <>
                                                <span
                                                    className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent"></span>
                                                Enhancing...
                                            </>
                                        ) : (
                                            <>
                                                <span aria-hidden>✦</span>
                                                Enhance with AI
                                            </>
                                        )}
                                    </button>
                                )}
                                {contentMode === 'write' && editingPrompt && (
                                    <button
                                        type="button"
                                        onClick={onGetMatchInsights}
                                        disabled={isLoadingMatchInsights}
                                        title="Get an advisory narrative based on recently-rejected URLs for this blueprint"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-ghost-bg px-3.5 py-1.5 text-xs font-bold text-accent-active disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoadingMatchInsights ? (
                                            <>
                                                <span
                                                    className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent"></span>
                                                Analysing rejected URLs...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-3 w-3" fill="none" stroke="currentColor"
                                                     viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round"
                                                          strokeWidth={2}
                                                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m12.728 0a9 9 0 11-12.728 0 9 9 0 0112.728 0z"/>
                                                </svg>
                                                Get insights
                                            </>
                                        )}
                                    </button>
                                )}
                                {/* Write / Generate with AI mode switch (bd deepcontext-0445.2),
                                    in the space freed by the removed dropzone. */}
                                <div
                                    role="tablist"
                                    aria-label="Content authoring mode"
                                    className="inline-flex rounded-full border border-line bg-ghost-bg p-0.5"
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={contentMode === 'write'}
                                        onClick={() => setContentMode('write')}
                                        className={`rounded-full px-3 py-1 text-xs font-bold ${contentMode === 'write' ? 'bg-accent-active text-white' : 'text-ink-muted'}`}
                                    >
                                        <span aria-hidden>✎</span> Write
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={contentMode === 'generate'}
                                        onClick={() => setContentMode('generate')}
                                        className={`rounded-full px-3 py-1 text-xs font-bold ${contentMode === 'generate' ? 'bg-accent-active text-white' : 'text-ink-muted'}`}
                                    >
                                        <span aria-hidden>✨</span> Generate with AI
                                    </button>
                                </div>
                            </div>
                        </div>

                        {contentLoadError && (
                            <div
                                className="mb-3 rounded-xl border border-risk bg-risk-bg px-3.5 py-2 text-xs text-risk"
                                role="alert"
                            >
                                {contentLoadError}
                            </div>
                        )}

                        {contentMode === 'generate' && (
                            <div className="mb-3 space-y-2">
                                <div className="flex items-end gap-2">
                                    <div className="flex-1">
                                        <label htmlFor="brandUrl" className={FIELD_LABEL}>
                                            Brand URL *
                                        </label>
                                        <input
                                            type="text"
                                            id="brandUrl"
                                            value={brandUrl}
                                            onChange={(e) => setBrandUrl(e.target.value)}
                                            placeholder="https://www.example.com"
                                            className={PILL_INPUT}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={onGenerateWithAI}
                                        disabled={isGeneratingPrompt || !brandUrl.trim() || formData.countryCodes.length === 0}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-accent h-[42px] px-4 py-2 text-xs font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isGeneratingPrompt ? (
                                            <>
                                                <span
                                                    className="h-3 w-3 animate-spin rounded-full border-b-2 border-white"></span>
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <span aria-hidden>✨</span>
                                                Generate
                                            </>
                                        )}
                                    </button>
                                </div>
                                {formData.countryCodes.length === 0 ? (
                                    <p className="text-xs text-warn">
                                        Select at least one country to generate
                                    </p>
                                ) : (
                                    <p className="text-xs text-ink-muted">
                                        Will generate for {formData.countryCodes.join(', ')} (from the Countries field above)
                                    </p>
                                )}
                            </div>
                        )}

                        <textarea
                            id="prompt"
                            required
                            value={formData.prompt}
                            onChange={(e) => setFormData({...formData, prompt: e.target.value})}
                            placeholder="Enter the detailed prompt for content analysis..."
                            rows={10}
                            className="w-full resize-none rounded-xl border border-line bg-transparent px-3 py-2.5 text-sm leading-relaxed text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                        />

                        {/* Match-insights panel (bd bqw.9). Renders only when the
                            user has clicked "Get insights" on an existing blueprint. */}
                        {editingPrompt && (isLoadingMatchInsights || matchInsights) && (
                            <MatchInsightsPanel
                                isLoading={isLoadingMatchInsights}
                                matchInsights={matchInsights}
                                onRerun={onGetMatchInsights}
                            />
                        )}
                    </div>

                    {formError && (
                        <div
                            data-testid="blueprint-form-error"
                            className="rounded-xl border border-risk bg-risk-bg px-3.5 py-2 text-xs text-risk"
                            role="alert"
                        >
                            {formError}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-2 border-t border-line pt-3">
                        <Button type="button" variant="ghost" size="md" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="default" size="md" disabled={isLoadingModal || allCategoriesExcluded}>
                            {editingPrompt ? 'Update Blueprint' : 'Create Blueprint'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BlueprintFormModal;
